import {Component, inject, Input, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {InputComponent} from '../form/input/input.component';
import {SelectComponent} from '../form/select/select.component';
import {EnumHelperService} from '../../@core/utils/forms/enum-helper.service';
import {PlzValidationHelperService} from '../../@core/utils/forms/plz-validation-helper.service';

export interface AddressFormData {
  street: string;
  addressAddition: string;
  postalCode: string;
  city: string;
  country: string;
}

/**
 * Wiederverwendbare Adress-Formularlogik - eingebettet sowohl im Verifizierungs-Wizard
 * (AddressStepComponent) als auch im AddressDetailsAdjustModalComponent.
 */
@Component({
  selector: 'app-address-details-form',
  standalone: true,
  imports: [TranslatePipe, ReactiveFormsModule, InputComponent, SelectComponent],
  templateUrl: './address-details-form.component.html',
})
export class AddressDetailsFormComponent implements OnInit {

  @Input() initialData: AddressFormData | null;
  // Erlaubt unterschiedliche data-cy-Präfixe je nach Einbettungskontext (Wizard-Step vs. Modal)
  @Input() testIdPrefix = 'address-data';

  form: UntypedFormGroup;
  protected landList = [];

  private readonly fb = inject(UntypedFormBuilder);
  private readonly enumHelperService = inject(EnumHelperService);
  protected readonly plzHelper = inject(PlzValidationHelperService);

  ngOnInit(): void {
    this.form = this.fb.group({
      street: [this.initialData?.street ?? '', [Validators.required]],
      city: [this.initialData?.city ?? '', [Validators.required]],
      postalCode: [this.initialData?.postalCode ?? '', [Validators.required]],
      country: [this.initialData?.country ?? null, [Validators.required]],
      addressAddition: [this.initialData?.addressAddition ?? '', []],
    });
    this.setPlzPatternValidator();
    this.landList = this.enumHelperService.getLandOptionListKV();
  }

  setPlzPatternValidator(): void {
    const land = this.form?.get('country')?.value;
    const postalCodeControl = this.form?.get('postalCode');
    if (postalCodeControl) {
      if (land) {
        postalCodeControl.setValidators([
          Validators.required,
          Validators.maxLength(this.plzHelper.getMaxPlzLength(land)),
          Validators.pattern(this.plzHelper.getPlzPattern(land))
        ]);
      } else {
        postalCodeControl.setValidators([Validators.required]);
      }
      postalCodeControl.updateValueAndValidity();
    }
  }

  get postalCodeErrorLabel(): string {
    if (this.form?.get('postalCode')?.hasError('required')) {
      return 'ADDRESS_DATA.FIELD_ERROR.REQUIRED';
    }
    return 'ADDRESS_DATA.FIELD_ERROR.POSTALCODE';
  }

  isValid(): boolean {
    return this.form.valid;
  }

  getFormValue(): AddressFormData {
    return this.form.value;
  }
}
